import { useEffect, useRef, useState, type Dispatch, type DragEvent, type SetStateAction } from 'react';
import { ApiError } from '../api/client';
import { LinkDialog } from './MailingDialogs';
import {
  BLOCK_LABEL, BUTTON_COLORS, cloneBlock, newBlock, renderBlock, sanitizeRich,
  type Align, type BlockType, type EmailBlock,
} from './email-blocks';

const PALETTE: BlockType[] = ['heading', 'text', 'image', 'button', 'divider', 'spacer'];
const PALETTE_ICON: Record<BlockType, string> = { heading: 'H', text: '¶', image: '▣', button: '▭', divider: '—', spacer: '↕' };

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function AlignPicker({ value, onChange }: { value: Align; onChange: (a: Align) => void }) {
  return (
    <div className="eb-seg">
      {(['left', 'center', 'right'] as Align[]).map((a) => (
        <button key={a} type="button" className={value === a ? 'active' : ''} onClick={() => onChange(a)}>{a === 'left' ? 'Vlevo' : a === 'center' ? 'Na střed' : 'Vpravo'}</button>
      ))}
    </div>
  );
}

function RichTextField({ blockId, html, onChange }: { blockId: string; html: string; onChange: (html: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeRich(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId]);
  const [linkOpen, setLinkOpen] = useState(false);
  const savedRange = useRef<Range | null>(null);
  const sync = () => { if (ref.current) onChange(ref.current.innerHTML); };
  const cmd = (c: string, v?: string) => { ref.current?.focus(); document.execCommand(c, false, v); sync(); };
  return (
    <div className="eb-rich">
      <div className="eb-rich-bar">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('bold'); }}><strong>B</strong></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('italic'); }}><em>I</em></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('underline'); }}><u>U</u></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('insertUnorderedList'); }}>• Odrážky</button>
        <button type="button" onMouseDown={(e) => {
          e.preventDefault();
          const sel = window.getSelection();
          savedRange.current = sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
          setLinkOpen(true);
        }}>Odkaz</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd('insertText', '[% shared.VOCATIVE %]'); }}>Oslovení</button>
      </div>
      {linkOpen && <LinkDialog onClose={() => setLinkOpen(false)} onSubmit={(url) => {
        setLinkOpen(false);
        ref.current?.focus();
        const sel = window.getSelection();
        if (savedRange.current) { sel?.removeAllRanges(); sel?.addRange(savedRange.current); }
        document.execCommand('createLink', false, url);
        sync();
      }} />}
      <div
        ref={ref}
        className="eb-rich-area"
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        onPaste={(e) => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); sync(); }}
      />
    </div>
  );
}

export function EmailBuilder({ upload: uploadFile, blocks, onChange, colors = BUTTON_COLORS, topLabel = 'Hlavička a oslovení ze šablony', bottomLabel = 'Patička s odhlášením ze šablony' }: { upload: (file: File) => Promise<string>; blocks: EmailBlock[]; onChange: Dispatch<SetStateAction<EmailBlock[]>>; colors?: string[]; topLabel?: string; bottomLabel?: string }) {
  const [selected, setSelected] = useState<string | null>(blocks[0]?.id ?? null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const canvas = useRef<HTMLDivElement>(null);

  const current = blocks.find((b) => b.id === selected) ?? null;
  const patch = (id: string, p: Partial<EmailBlock>) => onChange((cur) => cur.map((b) => (b.id === id ? ({ ...b, ...p } as EmailBlock) : b)));

  function insertAt(index: number, block: EmailBlock) {
    onChange((cur) => { const next = [...cur]; next.splice(index, 0, block); return next; });
    setSelected(block.id);
  }
  function moveTo(id: string, index: number) {
    onChange((cur) => {
      const from = cur.findIndex((b) => b.id === id);
      if (from < 0) return cur;
      const next = [...cur];
      const [item] = next.splice(from, 1);
      next.splice(index > from ? index - 1 : index, 0, item!);
      return next;
    });
  }
  function shift(id: string, dir: -1 | 1) {
    onChange((cur) => {
      const i = cur.findIndex((b) => b.id === id);
      const t = i + dir;
      if (i < 0 || t < 0 || t >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[t]] = [next[t]!, next[i]!];
      return next;
    });
  }
  function remove(id: string) {
    onChange((cur) => cur.filter((b) => b.id !== id));
    if (selected === id) setSelected(null);
  }
  function duplicate(id: string) {
    const i = blocks.findIndex((b) => b.id === id);
    if (i >= 0) insertAt(i + 1, cloneBlock(blocks[i]!));
  }

  function indexFromEvent(e: DragEvent): number {
    const items = [...(canvas.current?.querySelectorAll<HTMLElement>('.eb-block') ?? [])];
    for (let i = 0; i < items.length; i++) {
      const r = items[i]!.getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) return i;
    }
    return items.length;
  }
  function onDragOver(e: DragEvent) {
    e.preventDefault();
    setDropIndex(indexFromEvent(e));
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    const index = indexFromEvent(e);
    setDropIndex(null);
    if (data.startsWith('new:')) {
      const type = data.slice(4) as BlockType;
      if (PALETTE.includes(type)) insertAt(index, newBlock(type));
    } else if (data.startsWith('move:')) {
      moveTo(data.slice(5), index);
    }
  }

  async function upload(id: string, file: File | undefined) {
    if (!file) return;
    setUploadErr(null);
    if (file.size > 2 * 1024 * 1024) { setUploadErr(`Obrázek má ${(file.size / 1024 / 1024).toFixed(1)} MB, maximum jsou 2 MB.`); return; }
    setUploading(true);
    try {
      const url = await uploadFile(file);
      patch(id, { src: url, alt: file.name.replace(/\.[a-z0-9]+$/i, '') } as Partial<EmailBlock>);
    } catch (e) {
      setUploadErr(e instanceof ApiError ? e.message : 'Nahrání obrázku selhalo');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="eb-wrap">
      <aside className="eb-palette">
        <strong>Bloky</strong>
        <small>Přetáhni do e-mailu nebo klikni</small>
        {PALETTE.map((t) => (
          <button
            key={t}
            type="button"
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', `new:${t}`); e.dataTransfer.effectAllowed = 'copy'; }}
            onDragEnd={() => setDropIndex(null)}
            onClick={() => insertAt(blocks.length, newBlock(t))}
          >
            <span>{PALETTE_ICON[t]}</span>{BLOCK_LABEL[t]}
          </button>
        ))}
      </aside>

      <div className="eb-stage" onDragOver={onDragOver} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropIndex(null); }} onDrop={onDrop}>
        <div className="eb-canvas" ref={canvas}>
          <div className="eb-fixed">{topLabel}</div>
          {blocks.length === 0 && <div className={`eb-empty${dropIndex != null ? ' is-over' : ''}`}>Přetáhni sem první blok</div>}
          {blocks.map((b, i) => (
            <div key={b.id}>
              {dropIndex === i && <div className="eb-drop-line" />}
              <div
                className={`eb-block${selected === b.id ? ' is-selected' : ''}`}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', `move:${b.id}`); e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => setDropIndex(null)}
                onClick={() => setSelected(b.id)}
              >
                <div className="eb-block-tools" onClick={(e) => e.stopPropagation()}>
                  <span className="eb-handle" title="Přetáhni">⋮⋮</span>
                  <span className="eb-block-name">{BLOCK_LABEL[b.type]}</span>
                  <button type="button" title="Nahoru" disabled={i === 0} onClick={() => shift(b.id, -1)}>↑</button>
                  <button type="button" title="Dolů" disabled={i === blocks.length - 1} onClick={() => shift(b.id, 1)}>↓</button>
                  <button type="button" title="Duplikovat" onClick={() => duplicate(b.id)}>⧉</button>
                  <button type="button" title="Smazat" className="is-danger" onClick={() => remove(b.id)}>×</button>
                </div>
                {b.type === 'image' && !b.src
                  ? <div className="eb-image-empty">Obrázek - nahraj ho vpravo</div>
                  : <div className="eb-block-render" dangerouslySetInnerHTML={{ __html: renderBlock(b) }} />}
              </div>
            </div>
          ))}
          {dropIndex === blocks.length && blocks.length > 0 && <div className="eb-drop-line" />}
          <div className="eb-fixed">{bottomLabel}</div>
        </div>
      </div>

      <aside className="eb-props">
        {!current && <div className="eb-props-empty">Klikni na blok v e-mailu a uprav ho tady.</div>}
        {current && <strong className="eb-props-title">{BLOCK_LABEL[current.type]}</strong>}
        {current?.type === 'heading' && (
          <>
            <label>Text<input className="form-control form-control-sm" value={current.text} onChange={(e) => patch(current.id, { text: e.target.value })} /></label>
            <label>Velikost<div className="eb-seg">
              <button type="button" className={current.size === 'l' ? 'active' : ''} onClick={() => patch(current.id, { size: 'l' })}>Velký</button>
              <button type="button" className={current.size === 'm' ? 'active' : ''} onClick={() => patch(current.id, { size: 'm' })}>Střední</button>
            </div></label>
            <label>Zarovnání<AlignPicker value={current.align} onChange={(a) => patch(current.id, { align: a })} /></label>
          </>
        )}
        {current?.type === 'text' && (
          <>
            <label>Text</label>
            <RichTextField blockId={current.id} html={current.html} onChange={(html) => patch(current.id, { html })} />
            <label>Zarovnání<AlignPicker value={current.align} onChange={(a) => patch(current.id, { align: a })} /></label>
          </>
        )}
        {current?.type === 'image' && (
          <>
            {current.src && <img className="eb-image-preview" src={current.src} alt={current.alt} />}
            <label className="eb-upload">
              <input type="file" accept="image/png,image/jpeg,image/gif" disabled={uploading} onChange={(e) => { void upload(current.id, e.target.files?.[0]); e.target.value = ''; }} />
              <span>{uploading ? 'Nahrávám…' : current.src ? 'Nahrát jiný obrázek' : 'Nahrát obrázek'}</span>
            </label>
            <small className="text-muted">PNG, JPEG nebo GIF do 2 MB. Obrázek se uloží na naši doménu.</small>
            {uploadErr && <div className="text-danger small">{uploadErr}</div>}
            <label>Popis obrázku (alt)<input className="form-control form-control-sm" value={current.alt} onChange={(e) => patch(current.id, { alt: e.target.value })} /></label>
            <label>Odkaz po kliknutí<input className="form-control form-control-sm" value={current.href} placeholder="https://… (nepovinné)" onChange={(e) => patch(current.id, { href: e.target.value })} /></label>
            <label>Šířka {current.width} %<input type="range" min={20} max={100} step={5} value={current.width} onChange={(e) => patch(current.id, { width: Number(e.target.value) })} /></label>
            <label>Zarovnání<AlignPicker value={current.align} onChange={(a) => patch(current.id, { align: a })} /></label>
          </>
        )}
        {current?.type === 'button' && (
          <>
            <label>Text tlačítka<input className="form-control form-control-sm" value={current.label} onChange={(e) => patch(current.id, { label: e.target.value })} /></label>
            <label>Odkaz<input className="form-control form-control-sm" value={current.href} onChange={(e) => patch(current.id, { href: e.target.value })} /></label>
            <label>Šířka<div className="eb-seg">
              {([['narrow', 'Užší'], ['normal', 'Běžná'], ['wide', 'Širší'], ['full', 'Celá šířka']] as const).map(([v, l]) => (
                <button key={v} type="button" className={(current.size ?? 'normal') === v ? 'active' : ''} onClick={() => patch(current.id, { size: v })}>{l}</button>
              ))}
            </div></label>
            <label>Tvar<div className="eb-seg">
              {([['square', 'Hranaté'], ['rounded', 'Zaoblené'], ['pill', 'Kulaté']] as const).map(([v, l]) => (
                <button key={v} type="button" className={(current.shape ?? 'rounded') === v ? 'active' : ''} onClick={() => patch(current.id, { shape: v })}>{l}</button>
              ))}
            </div></label>
            <label>Barva<div className="eb-colors">
              {colors.map((c) => <button key={c} type="button" title={c} className={current.color === c ? 'active' : ''} style={{ background: c }} onClick={() => patch(current.id, { color: c })} />)}
            </div></label>
            <label>Zarovnání<AlignPicker value={current.align} onChange={(a) => patch(current.id, { align: a })} /></label>
          </>
        )}
        {current?.type === 'spacer' && (
          <label>Výška {current.height} px<input type="range" min={8} max={80} step={4} value={current.height} onChange={(e) => patch(current.id, { height: Number(e.target.value) })} /></label>
        )}
        {current?.type === 'divider' && <small className="text-muted">Oddělovač nemá nastavení.</small>}
      </aside>
    </div>
  );
}
